/**
 * Two routes, and one of them is the whole reason there is a router.
 *
 * `decisions.md` locks "TanStack Router used standalone as a library", and this
 * is what standalone means: routes defined in code, no file-based convention, no
 * build plugin, no framework taking over the entry point. It is used for
 * exactly what a router is for here — turning a pasted URL into an open
 * message, and making the back button work.
 *
 * `/m/$ref` IS THE PERMALINK. The Worker's assets are configured
 * `not_found_handling: "single-page-application"`, so every path that is not
 * under `/_hx/` is answered with `index.html` and a 200 — which means this file
 * is what decides that `/m/R0ZQFTR051QFMBMR` opens a message rather than
 * rendering an empty shell.
 *
 * A REF IS AN IDENTIFIER, NOT A CAPABILITY. Arriving here with one grants
 * nothing: the SPA is behind Cloudflare Access like everything else, and the
 * origin resolves the ref only within the caller's own account. A permalink in
 * a chat log is a dead string to anybody who is not already in the household.
 */
import { createRootRoute, createRoute, createRouter, Outlet, RouterProvider } from '@tanstack/react-router'
import { App } from './app.tsx'

const root = createRootRoute({ component: Outlet })

const home = createRoute({
  getParentRoute: () => root,
  path: '/',
  component: () => <App />,
})

/**
 * The permalink route.
 *
 * The ref is handed to the shell rather than resolved here, because resolving
 * it needs the authenticated fetch the shell already owns — and because a
 * failure has to render INSIDE the shell (with the search box and the nav still
 * there) rather than as a bare error page. A permalink that has expired,
 * belongs to somebody else, or was mistyped should leave the reader somewhere
 * they can carry on from.
 */
const permalink = createRoute({
  getParentRoute: () => root,
  path: '/m/$ref',
  component: function Permalink() {
    const { ref } = permalink.useParams()
    return <App openRef={ref} />
  },
})

/**
 * A catch-all, because the SPA is served for EVERY path.
 *
 * Without it, a typo under the app's own origin renders nothing at all — the
 * assets binding has already answered 200 with index.html, so there is no 404
 * anywhere for the reader to see. Falling back to the shell means a wrong URL
 * costs them a search box rather than a blank page.
 */
const anything = createRoute({
  getParentRoute: () => root,
  path: '$',
  component: () => <App />,
})

const tree = root.addChildren([home, permalink, anything])

export const router = createRouter({ routeTree: tree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function Routes(): React.JSX.Element {
  return <RouterProvider router={router} />
}
